import { useEffect, useState } from "react";
import { generatePath, Link } from "react-router-dom";
import { Plus } from "lucide-react";

import { cn } from "@/libs/cn";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import { Skeleton, SortDropdownValue, Tooltip } from "@/modules/common/components";
import Button from "@/modules/common/components/files/Button";
import EntityGridList from "@/modules/common/components/files/EntityGridList";
import { GridTableCell, GridTableScroll } from "@/modules/common/components/files/GridTable";
import PageBreadcrumb from "@/modules/common/components/files/PageBreadcrumb";
import Tag from "@/modules/common/components/files/Tag";
import { useFilters } from "@/modules/common/filters";
import { FILTER_KEYS } from "@/modules/common/filters/keys";
import { usePagination } from "@/modules/common/hooks/usePagination";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import { formatDate } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";
import { ListAdminKnowledgeBaseDto } from "@/service/dto/admin-knowledge-base";
import { KnowledgeBaseContentListItemResponse } from "@/service/response/admin-knowledge-base";

import KnowledgeBaseFilterBar from "../components/KnowledgeBaseFilterBar";
import {
  knowledgeBaseAudienceLabel,
  knowledgeBaseCategoryLabel,
  KnowledgeBaseSortField,
  KnowledgeBaseStatus,
  knowledgeBaseStatusLabel,
  knowledgeBaseTypeLabel,
} from "../constants";
import { KNOWLEDGE_BASE_FILTER_FIELDS } from "../filter-fields";
import useKnowledgeBaseList from "../hooks/useKnowledgeBaseList";

const gridCols = "md:grid-cols-[3.2fr_1.6fr_1.5fr_1.7fr_1.3fr_1.4fr_1.6fr]";
const gridGap = "md:gap-4";
const gridLayout = cn("flex flex-col gap-2 md:grid md:items-center md:min-h-12", gridCols, gridGap, "px-4 md:px-6 py-3 md:py-2");
const cellText = "text-sm text-ui-navy-dark md:min-w-0";
const headerLabels = ["Título", "Categoria", "Tipo", "Público", "Status", "Atualizado em", "Atualizado por"];

const statusVariant = (status: number) => {
  if (status === KnowledgeBaseStatus.PUBLISHED) return "success" as const;
  if (status === KnowledgeBaseStatus.ARCHIVED) return "neutral" as const;
  return "warning" as const;
};

const KnowledgeBaseList = () => {
  const { canUserSee } = useSessionStore();
  const canWrite = canUserSee(AdminUserRole.ADMIN);
  const [sort, setSort] = useState<SortDropdownValue<KnowledgeBaseSortField> | null>(null);

  const filters = useFilters<ListAdminKnowledgeBaseDto>({
    fields: KNOWLEDGE_BASE_FILTER_FIELDS,
    filterKey: FILTER_KEYS.KNOWLEDGE_BASE,
  });

  const { page, setPage, limit, setPageSize } = usePagination();

  const filtersKey = JSON.stringify(filters.queryParams);
  const sortKey = sort ? `${sort.field}:${sort.order}` : "";
  useEffect(() => {
    setPage(1);
  }, [filtersKey, sortKey]);

  const query = useKnowledgeBaseList({
    ...filters.queryParams,
    sortBy: sort?.field ?? KnowledgeBaseSortField.UPDATED_AT,
    sortOrder: sort?.order ?? "desc",
    page,
    limit,
  });

  return (
    <div className="flex flex-col gap-4">
      <PageBreadcrumb items={[{ label: "Central de Ajuda", to: ROUTES.HELP.HOME }, { label: "Base de Conhecimento" }]} />

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-ui-primary">Base de Conhecimento</h1>
          <p className="mt-1 text-sm text-ui-text-muted">Artigos e tutoriais publicados nos portais da gestora e do cliente.</p>
        </div>
        {canWrite && <Button route={ROUTES.KNOWLEDGE_BASE.ADD} icon={<Plus size={16} />} label="Novo conteúdo" />}
      </header>

      <KnowledgeBaseFilterBar filters={filters} sort={sort} onSortChange={setSort} />

      {query.isError && !query.data ? (
        <div className="rounded-lg bg-white p-8 text-center shadow">
          <p className="text-sm text-ui-text-muted">Não foi possível carregar os conteúdos.</p>
          <Button
            type="button"
            variant="text"
            color="primary"
            size="small"
            label="Tentar novamente"
            className="mx-auto mt-2"
            onClick={() => void query.refetch()}
          />
        </div>
      ) : (
        <EntityGridList
          data={query.data}
          isLoading={query.isLoading}
          isFetching={query.isFetching}
          limit={limit}
          minWidthClassName="md:min-w-[980px]"
          onChangePage={setPage}
          onChangePageSize={setPageSize}
          renderHeader={() => (
            <div className={cn("hidden border-b border-gray-100 bg-white md:grid md:items-center", gridCols, gridGap, "px-6 py-4")}>
              {headerLabels.map((label) => (
                <span key={label} className="truncate text-sm font-semibold text-gray-500">
                  {label}
                </span>
              ))}
            </div>
          )}
          renderSkeleton={(rows) => (
            <div className="relative w-full overflow-hidden rounded-lg bg-white shadow">
              <GridTableScroll minWidthClassName="md:min-w-[980px]">
                {Array.from({ length: rows }).map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      gridLayout,
                      "rounded-lg border border-[#D6DEE9] bg-white md:rounded-none md:border-0 md:border-t md:bg-transparent",
                    )}
                  >
                    {headerLabels.map((label) => (
                      <Skeleton key={label} className="h-4 w-full" />
                    ))}
                  </div>
                ))}
              </GridTableScroll>
            </div>
          )}
          renderRow={(item) => <KnowledgeBaseRow item={item} />}
        />
      )}
    </div>
  );
};

const KnowledgeBaseRow = ({ item }: { item: KnowledgeBaseContentListItemResponse }) => (
  <Link
    to={generatePath(ROUTES.KNOWLEDGE_BASE.EDIT, { id: item.id })}
    className={cn(
      gridLayout,
      "rounded-lg border border-[#D6DEE9] bg-white transition-colors hover:bg-ui-hover md:rounded-none md:border-0 md:border-t md:bg-transparent",
    )}
  >
    <GridTableCell label="Título" className={cellText}>
      <Tooltip label={item.title}>
        <span className="block truncate font-medium">{item.title}</span>
      </Tooltip>
    </GridTableCell>
    <GridTableCell label="Categoria" className={cellText}>
      <span className="block truncate">{knowledgeBaseCategoryLabel(item.category)}</span>
    </GridTableCell>
    <GridTableCell label="Tipo" className={cellText}>
      <span className="block truncate">{knowledgeBaseTypeLabel(item.type)}</span>
    </GridTableCell>
    <GridTableCell label="Público" className={cellText}>
      <span className="block truncate">{knowledgeBaseAudienceLabel(item.audiences)}</span>
    </GridTableCell>
    <GridTableCell label="Status">
      <Tag label={knowledgeBaseStatusLabel(item.status)} variant={statusVariant(item.status)} />
    </GridTableCell>
    <GridTableCell label="Atualizado em" className={cellText}>
      {formatDate(item.updatedAt, "DD/MM/YYYY")}
    </GridTableCell>
    <GridTableCell label="Atualizado por" className={cellText}>
      <Tooltip label={item.updatedBy.name}>
        <span className="block truncate">{item.updatedBy.name}</span>
      </Tooltip>
    </GridTableCell>
  </Link>
);

export default KnowledgeBaseList;
